import {
  retrieveAtomicPublicToken,
  validateAndUpdateCashPinToken,
} from 'actions/sesameCash.actions';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { useLogger } from 'hook/error.hook';
import { useFlag } from 'hook/flag.hook';
import React, { useEffect } from 'react';
import { DIRECT_DEPOSIT_AUTOMATIC } from 'types/morph.pagePosition.constants';
import DirectDepositAutomaticView from './DirectDepositAutomaticView';
import {
  useComplexState,
  useSeenModulePixel,
  useViewModule,
} from 'hook/helper.hook';
import { getViewModuleTrackingAttributes } from 'helpers/tracking.helpers';
import { BANKING_VERTICAL } from 'types/mrph.vertical.constants';

const DirectDepositAutomaticControlller = props => {
  const { dispatch, eventHandler } = props;
  const [state, setState] = useComplexState({
    loading: false,
    showUpdateDirectDepositInfoModal: false,
    showAtomicSdk: false,
    publicToken: null,
    cashPinTokenValid: false,
    pinTokenRequestAttempts: 0,
  });
  const logger = useLogger({ name: 'DirectDepositManualController' });
  const atr = getViewModuleTrackingAttributes({
    pagePosition: DIRECT_DEPOSIT_AUTOMATIC,
    modulePosition: props.modulePosition,
    moduleType: DIRECT_DEPOSIT_AUTOMATIC,
    vertical: BANKING_VERTICAL,
  });
  useViewModule(atr);
  const seenPixel = useSeenModulePixel(atr);

  const cashFlag = useFlag('cash');

  const { atomic_sdk_integration, module_direct_deposit_automatic } =
    cashFlag.config;

  const moduleConfig =
    module_direct_deposit_automatic?.atomicConfigAllSegments ?? {};

  const onClickSetupDirectDeposit = () => {
    if (state.loading) return;
    if (!atomic_sdk_integration) {
      logger.showError(
        'The direct deposit switch service is unavailable at the moment. Please check back again soon.',
      );
    } else {
      eventHandler(CLICK, {
        'Click Type': 'Set Up Direct Deposit',
        Vertical: BANKING_VERTICAL,
        ...atr,
      });
      if (!state.showAtomicSdk) {
        setState({ loading: true });
        dispatch(validateAndUpdateCashPinToken({ state, setState }));
      }
    }
  };

  const onClickTermsOfUse = () => {
    eventHandler(CLICK, {
      'Click Type': 'Terms of Use',
      Vertical: BANKING_VERTICAL,
    });
    setState({ showUpdateDirectDepositInfoModal: true });
  };

  const onClickCancelInfoModal = () => {
    setState({ showUpdateDirectDepositInfoModal: false });
  };

  const onClickUpdateDirectDeposit = () => {
    setState({ showUpdateDirectDepositInfoModal: false });
    onClickSetupDirectDeposit();
  };

  const filterAtomicEvents = (name, value) => {};

  const onAtomicFinish = data => {
    setState({
      showAtomicSdk: false,
      publicToken: null,
    });
    props?.history.push(
      '/cash/directDepositInformation/autoLanding?status=success',
    );
  };

  const onAtomicClose = data => {
    setState({
      showAtomicSdk: false,
      publicToken: null,
    });
    if (data?.reason === 'task-pending') {
      props?.history.push(
        '/cash/directDepositInformation/autoLanding?status=pending',
      );
    } else if (data?.reason === 'zero-search-results') {
      props?.history.push('/cash/directDepositInformation?type=MANUAL');
    } else {
      props?.history.push('/cash/directDepositInformation?type=AUTOMATIC');
    }
  };

  const tryToShowAtomicSDK = () => {
    dispatch(retrieveAtomicPublicToken())
      .then(tokenResponse => {
        setState({
          cashPinTokenValid: false,
          showAtomicSdk: true,
          loading: false,
          publicToken: tokenResponse.publicToken,
          pinTokenRequestAttempts: 0,
        });
      })
      .catch(err => {
        logger.reportAPIError(err);
        setState({
          loading: false,
          cashPinTokenValid: false,
          pinTokenRequestAttempts: 0,
        });
      });
  };

  useEffect(() => {
    if (state.cashPinTokenValid) {
      tryToShowAtomicSDK();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cashPinTokenValid]);

  useEffect(() => {
    if (state.pinTokenRequestAttempts > 0) {
      if (state.pinTokenRequestAttempts < 3) {
        setState({ loading: true, cashPinTokenValid: false });
        dispatch(validateAndUpdateCashPinToken({ state, setState }));
      } else {
        setState({ loading: false, pinTokenRequestAttempts: 0 });
        logger.showError(
          'Unable to setup a direct deposit. Please contact support for help.',
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pinTokenRequestAttempts]);

  return (
    <DirectDepositAutomaticView
      {...state}
      {...moduleConfig}
      onClickCancelInfoModal={onClickCancelInfoModal}
      onClickTermsOfUse={onClickTermsOfUse}
      onClickSetupDirectDeposit={onClickSetupDirectDeposit}
      onClickUpdateDirectDeposit={onClickUpdateDirectDeposit}
      filterAtomicEvents={filterAtomicEvents}
      onAtomicFinish={onAtomicFinish}
      onAtomicClose={onAtomicClose}
      seenPixel={seenPixel}
    />
  );
};

export default DirectDepositAutomaticControlller;
